import { motion } from 'framer-motion'
import { CreditCard, Snowflake, Wifi, Radio } from 'lucide-react'
import { Card } from '@/types'
import { cn, maskCardNumber, getCardGradient } from '@/lib/utils'

interface VirtualCardProps {
  card: Card
  isActive?: boolean
  onClick?: () => void
  className?: string
}

export default function VirtualCard({ 
  card, 
  isActive = false,
  onClick,
  className 
}: VirtualCardProps) {
  const isFrozen = card.status === 'frozen'
  
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={cn(
        "relative w-full aspect-[1.586] rounded-2xl overflow-hidden cursor-pointer",
        "transition-shadow duration-300",
        isActive ? 'shadow-glow-lg' : 'shadow-card hover:shadow-card-hover',
        className
      )}
    >
      {/* Card background */}
      <div className={cn(
        "absolute inset-0 bg-gradient-to-br",
        getCardGradient(card.color)
      )} />
      
      {/* Shine effect */}
      <div className="absolute inset-0 bg-gradient-shine opacity-20" />
      
      {/* Pattern overlay */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" viewBox="0 0 400 250">
          <pattern id={`pattern-${card.id}`} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="20" cy="20" r="1" fill="white" />
          </pattern>
          <rect width="100%" height="100%" fill={`url(#pattern-${card.id})`} />
        </svg>
      </div>
      
      {/* Frozen overlay */}
      {isFrozen && (
        <div className="absolute inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <Snowflake className="w-8 h-8 text-white" />
            <span className="text-white font-medium">Frozen</span>
          </div>
        </div>
      )}
      
      {/* Card content */}
      <div className="relative z-10 h-full p-5 flex flex-col justify-between text-white">
        {/* Top row */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Radio className="w-6 h-6 opacity-80" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium opacity-80 uppercase tracking-wider">
              {card.tier}
            </span>
            <Wifi className="w-5 h-5 opacity-80 rotate-90" />
          </div>
        </div>
        
        {/* Middle - Card number */}
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <CreditCard className="w-8 h-8 opacity-60" />
          </div>
          <p className="text-xl font-mono tracking-wider">
            {maskCardNumber(card.lastFour)}
          </p>
        </div>
        
        {/* Bottom row */}
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xs opacity-60 mb-1">Cardholder</p>
            <p className="font-medium text-sm tracking-wide">
              {card.cardholderName.toUpperCase()}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs opacity-60 mb-1">Expires</p>
            <p className="font-mono text-sm">
              {card.expiryMonth}/{card.expiryYear}
            </p>
          </div>
        </div>
      </div>
      
      {/* Active indicator */}
      {isActive && (
        <motion.div
          layoutId="activeCard"
          className="absolute inset-0 rounded-2xl ring-2 ring-white/50"
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      )}
    </motion.div>
  )
}